import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "@/App.css";
import "@/theme-white.css";
import "@/theme-sage.css";
import "@/theme-panna.css";
import "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";
import { setLenis, scrollToId } from "@/lib/scroll";
import { Nav } from "@/components/site/Nav";
import { Hero } from "@/components/site/Hero";
import { SeoSicilia } from "@/components/site/SeoSicilia";
import { Marquee } from "@/components/site/Marquee";
import { LamellareIntro } from "@/components/site/LamellareIntro";
import { HomeFaq } from "@/components/site/HomeFaq";
import { AboutTeaser } from "@/components/site/AboutTeaser";
import { Products } from "@/components/site/Products";
import { Models } from "@/components/site/Models";
import { Projects } from "@/components/site/Projects";
import { Process } from "@/components/site/Process";
import { OneStop } from "@/components/site/OneStop";
import { WhyEmma } from "@/components/site/WhyEmma";
import { CustomBeams } from "@/components/site/CustomBeams";
import { GuideTeaser } from "@/components/site/GuideTeaser";
import { HealthyHome } from "@/components/site/HealthyHome";
import { Chapter, ChapterIndex } from "@/components/site/Chapters";
import { TrustBand } from "@/components/site/TrustBand";
import LegalPage from "@/pages/LegalPage";
import LavoraConNoi from "@/pages/LavoraConNoi";
import JobsManage from "@/pages/JobsManage";
import { BioHouse } from "@/components/site/BioHouse";
import { CasaInLegno } from "@/components/site/CasaInLegno";
import { SheetDownloads } from "@/components/site/SheetDownloads";
import { Beach } from "@/components/site/Beach";
import { Construction } from "@/components/site/Construction";
import GuidaTecnica from "@/pages/GuidaTecnica";
import { Sustainability } from "@/components/site/Sustainability";
import { Certifications } from "@/components/site/Certifications";
import { Presence } from "@/components/site/Presence";
import { VideoReel } from "@/components/site/VideoReel";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { Locations } from "@/components/site/Locations";
import ChiSiamo from "@/pages/ChiSiamo";
import StabilimentiBalneari from "@/pages/StabilimentiBalneari";
import EntiPubblici from "@/pages/EntiPubblici";
import { LangProvider, LangUrlSync, useT } from "@/i18n";
import { usePageMeta } from "@/lib/pageMeta";
import LegnoLamellare from "@/pages/LegnoLamellare";
import PannelliXlam from "@/pages/PannelliXlam";
import AreaRiservata from "@/pages/AreaRiservata";
import CompetitorsManage from "@/pages/CompetitorsManage";
import FaqDraft from "@/pages/FaqDraft";
import LettiereAnimali from "@/pages/LettiereAnimali";
import Biomassa from "@/pages/Biomassa";
import MappaSito from "@/pages/MappaSito";
import CookiePolicy from "@/pages/CookiePolicy";
import { CookieBanner } from "@/components/site/CookieBanner";
import { PaletteSwitch } from "@/components/site/PaletteSwitch";

const HomeMeta = () => {
  const t = useT();
  usePageMeta({
    title: t("home.metaTitle", "Legno Lamellare Strutturale e per Ingegneria"),
    desc: t("home.metaDesc", "Legno lamellare, case prefabbricate e strutture in legno in Sicilia: progettazione e realizzazione chiavi in mano, tetti, verande e travi. EMMA INDUSTRIE."),
    ogTitle: t("home.ogTitle", "Soluzioni in Legno Lamellare d'Eccellenza - EMMA INDUSTRIE"),
    ogDesc: t("home.ogDesc", "Libertà di configurazione illimitata, resistenza al fuoco e grandi luci. Scopri i sistemi strutturali in legno lamellare firmati EMMA INDUSTRIE per l'edilizia sostenibile."),
    imageAlt: t("home.ogAlt", "Soluzioni in legno lamellare d'eccellenza EMMA INDUSTRIE: cupola in legno lamellare"),
    brand: "EMMA INDUSTRIE",
    path: "/",
    image: "/og-home.jpg",
  });
  return null;
};

const Original = () => (
  <div className="App bg-[#121110]">
    <HomeMeta />
    <Nav />
    <main>
      <Chapter n={1} caption={false}>
        <Hero />
        <Marquee />
      </Chapter>
      <Chapter n={2}>
        <Products />
        <VideoReel />
        <TrustBand />
        <SheetDownloads keys={["catalogo"]} />
        <CustomBeams />
        <GuideTeaser />
      </Chapter>
      <Chapter n={3}>
        <CasaInLegno />
        <Models />
        <HealthyHome />
        <BioHouse />
      </Chapter>
      <Chapter n={4}>
        <LamellareIntro />
        <OneStop />
      </Chapter>
      <Chapter n={5}>
        <AboutTeaser />
        <WhyEmma />
        <Presence />
      </Chapter>
      <Chapter n={6}>
        <Beach />
      </Chapter>
      <Chapter n={7}>
        <Projects />
        <Construction />
      </Chapter>
      <Chapter n={8}>
        <Process />
        <Certifications />
        <Sustainability />
      </Chapter>
      <Chapter n={9}>
        <HomeFaq />
        <SeoSicilia />
      </Chapter>
      <Chapter n={10}>
        <Contact />
        <Locations />
      </Chapter>
    </main>
    <ChapterIndex />
    <Footer />
  </div>
);

let lenisInstance = null;

const ScrollReset = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t1 = setTimeout(() => scrollToId(hash), 400);
      const t2 = setTimeout(() => scrollToId(hash), 1400);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (lenisInstance) lenisInstance.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisInstance = lenis;
    setLenis(lenis);
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisInstance = null;
      setLenis(null);
    };
  }, []);

  return (
    <LangProvider>
    <BrowserRouter>
      <ScrollReset />
      <LangUrlSync />
      <Routes>
        <Route path="/" element={<Original />} />
        <Route path="/chi-siamo" element={<ChiSiamo />} />
        <Route path="/stabilimenti-balneari" element={<StabilimentiBalneari />} />
        <Route path="/enti-pubblici" element={<EntiPubblici />} />
        <Route path="/legno-lamellare" element={<LegnoLamellare />} />
        <Route path="/pannelli-xlam" element={<PannelliXlam />} />
        <Route path="/guida-tecnica" element={<GuidaTecnica />} />
        <Route path="/area-riservata" element={<AreaRiservata />} />
        <Route path="/area-riservata/concorrenti/:token" element={<CompetitorsManage />} />
        <Route path="/lettiere-animali" element={<LettiereAnimali />} />
        <Route path="/biomassa" element={<Biomassa />} />
        <Route path="/mappa-del-sito" element={<MappaSito />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/privacy-policy" element={<LegalPage doc="privacy" />} />
        <Route path="/termini-e-condizioni" element={<LegalPage doc="terms" />} />
        <Route path="/lavora-con-noi" element={<LavoraConNoi />} />
        <Route path="/area-riservata/posizioni/:token" element={<JobsManage />} />
        <Route path="/area-riservata/faq/:token" element={<FaqDraft />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <WhatsAppButton />
      <CookieBanner />
      <PaletteSwitch />
      <Toaster position="top-center" />
    </BrowserRouter>
    </LangProvider>
  );
}

export default App;
